/**
 * RevenueHubPage.js — Unified revenue-cycle surface.
 *
 * Phase 1 of the Operations-section revenue consolidation. The previous
 * sidebar exposed five separate top-level entries for what is really one
 * workflow (invoice → estimate → collect payment → reconcile → analyze):
 *
 *   • Billing             → invoices, statements
 *   • Estimates           → patient cost estimates
 *   • Stripe Payments     → live payment processing
 *   • Stripe Reports      → payment-level reporting
 *   • Payment Analytics   → cross-payment trend analysis
 *
 * Each one is genuinely distinct, but staff bounce between them constantly
 * during the same task ("create estimate → convert to invoice → take
 * payment → check the analytics") and the five sidebar items make that
 * flow feel fragmented. This page collapses them into one entry "Revenue"
 * with five tabs.
 *
 * Insurance is INTENTIONALLY NOT included here. It has its own internal
 * tabs/dashboard (1655 LOC of nested UI) and is conceptually about
 * eligibility/authorization more than money flow. Folding it in would
 * create double-nested tabs and break the encapsulation we're trying
 * to add. Insurance stays as its own sidebar entry.
 *
 * Architecture: thin shell, embeds existing pages, zero backend or schema
 * changes. Same playbook as Messages and Telehealth hubs.
 */
import React, { useState } from 'react';
import { Receipt, FileText, CreditCard, FileBarChart, BarChart3, Lock } from 'lucide-react';
import BillingPage from './BillingPage';
import BillingEstimatesPage from './BillingEstimatesPage';
import StripePage from './StripePage';
import StripeReportsPage from './StripeReportsPage';
import PaymentAnalyticsPage from './PaymentAnalyticsPage';

const TABS = [
  { id: 'invoices',   label: 'Invoices',        icon: Receipt,      perm: 'billing:read' },
  { id: 'estimates',  label: 'Estimates',       icon: FileText,     perm: 'billing:read' },
  { id: 'stripe',     label: 'Stripe Payments', icon: CreditCard,   perm: 'billing:read' },
  { id: 'reports',    label: 'Stripe Reports',  icon: FileBarChart, perm: 'billing:read' },
  { id: 'analytics',  label: 'Analytics',       icon: BarChart3,    perm: 'billing:read' },
];

export default function RevenueHubPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  // Invoices first — that's the most-frequented surface for billing staff.
  // Daniel pointed out during the consolidation audit that the Pipeline UI
  // was "linear and not easily usable"; the goal here is a stable left-to-
  // right reading order matching the real revenue lifecycle.
  const defaultTab = visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'invoices';

  const [activeTab, setActiveTab] = useState(defaultTab);

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Revenue</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Lock size={48} />
          <p>You don't have permission to view revenue surfaces.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page revenue-hub-page">
      <div className="revenue-hub-header">
        <h1 className="page-title">Revenue</h1>
      </div>

      <div className="revenue-hub-tabs" role="tablist" aria-label="Revenue sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`revenue-hub-panel-${tab.id}`}
              className={`revenue-hub-tab ${isActive ? 'revenue-hub-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="revenue-hub-panel"
        id={`revenue-hub-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'invoices' && (
          <div className="revenue-hub-embedded">
            <BillingPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'estimates' && (
          <div className="revenue-hub-embedded">
            <BillingEstimatesPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'stripe' && (
          <div className="revenue-hub-embedded">
            <StripePage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'reports' && (
          <div className="revenue-hub-embedded">
            <StripeReportsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'analytics' && (
          <div className="revenue-hub-embedded">
            <PaymentAnalyticsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
      </div>

      <style jsx>{`
        .revenue-hub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .revenue-hub-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .revenue-hub-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .revenue-hub-tab:hover {
          color: #2E8B98;
        }
        .revenue-hub-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        .revenue-hub-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .revenue-hub-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}
